import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, TextField, Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { ValidationError, getApiErrorMessage, getValidationErrors } from '@/lib/validationErrors';

interface BuildDiagnostic {
  severity: 'INFO' | 'WARNING' | 'ERROR';
  message: string;
}

interface SetupPackageBuildDialogProps {
  /** The module and closure builder that packages the setup. */
  moduleName: string;
  rootEntityName: string;
  /** The record the closure is built around; absent for a setup that is the tenant's as a whole. */
  rootId?: string;
  /** For a builder without a root: the records to package instead of the whole setup. */
  selection?: { entityName: string; ids: string[] };
  title: string;
  /** What the package will contain, shown above the name. */
  intro: ReactNode;
  open: boolean;
  onClose: () => void;
}

/**
 * Builds a Setup Package from a module's closure builder (spec:
 * docs/15-designspecs/Setup-Packages.md) and stores it as a package that can be applied here or
 * exported to another environment. What the builder could not carry (a secret, a reference with
 * no transferable key) comes back as a warning and is shown before the hand-over.
 */
export function SetupPackageBuildDialog({
  moduleName, rootEntityName, rootId, selection, title, intro, open, onClose,
}: SetupPackageBuildDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);

  const buildMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{ package: { id: string }; diagnostics: BuildDiagnostic[] }>(
        '/api/core/setuppackage/build',
        {
          moduleName,
          rootEntityName,
          rootId: rootId ?? null,
          selection: selection ?? null,
          name: name.trim(),
          description: description.trim() || null,
        },
      );
      return response.data;
    },
    onSuccess: (built) => {
      setCreatedId(built.package.id);
      // INFO diagnostics (e.g. "identical package reused") need no attention.
      setWarnings(built.diagnostics.filter((d) => d.severity !== 'INFO').map((d) => d.message));
      setApiValidationErrors([]);
    },
    onError: (error: unknown) => {
      const errors = getValidationErrors(error);
      if (errors.length > 0) {
        setApiValidationErrors(errors);
        return;
      }
      const message = getApiErrorMessage(error)
        ?? t('common.genericError', { defaultValue: 'An error occurred' });
      setApiValidationErrors([{ field: 'form', message, instancePath: '#', members: [] }]);
    },
  });

  const handleClose = () => {
    setName('');
    setDescription('');
    setCreatedId(null);
    setWarnings([]);
    setApiValidationErrors([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogValidationErrors errors={apiValidationErrors} onClear={() => setApiValidationErrors([])} />
        {createdId ? (
          <>
            <Alert
              severity="success"
              action={
                <Button size="small" onClick={() => { handleClose(); navigate(`/core/setuppackages/${createdId}`); }}>
                  {t('Core.SetupPackage.OpenPackage', { defaultValue: 'Open the package' })}
                </Button>
              }
            >
              {t('Core.SetupPackage.BuildSuccess', {
                defaultValue: 'Package saved. Export it as a file to move the setup to another environment.',
              })}
            </Alert>
            {warnings.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {t('Core.SetupPackage.BuildWarnings', {
                  defaultValue: 'Some content could not travel:',
                })}
                <List dense sx={{ py: 0.5 }}>
                  {warnings.map((warning) => <ListItem key={warning} sx={{ py: 0 }}>{warning}</ListItem>)}
                </List>
              </Alert>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{intro}</Typography>
            <TextField
              fullWidth
              required
              autoFocus
              label={t('Core.SetupPackage.BuildName', { defaultValue: 'Package name' })}
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t('Core.SetupPackage.BuildDescription', { defaultValue: 'Description (optional)' })}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {createdId
            ? t('common.close', { defaultValue: 'Close' })
            : t('common.cancel', { defaultValue: 'Cancel' })}
        </Button>
        {!createdId && (
          <Button
            variant="contained"
            disabled={!name.trim() || buildMutation.isPending}
            onClick={() => buildMutation.mutate()}
          >
            {t('Core.SetupPackage.BuildConfirm', { defaultValue: 'Save package' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
